import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdClose, MdTune } from 'react-icons/md';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useLanguage } from '../../context/LanguageContext';

export default function SensorThresholdModal({ sensor, onClose, onSuccess }) {
    const { t } = useLanguage();
    const [minThreshold, setMinThreshold] = useState(sensor.minThreshold ?? '');
    const [maxThreshold, setMaxThreshold] = useState(sensor.maxThreshold ?? '');
    const [thresholdEditableByCompany, setThresholdEditableByCompany] = useState(!!sensor.thresholdEditableByCompany);
    const [dataGapAlertEnabled, setDataGapAlertEnabled] = useState(sensor.dataGapAlertEnabled !== false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (minThreshold !== '' && Number.isNaN(Number(minThreshold))) { setError(t('sensorThresholdModal.invalidNumber')); return; }
        if (maxThreshold !== '' && Number.isNaN(Number(maxThreshold))) { setError(t('sensorThresholdModal.invalidNumber')); return; }
        if (minThreshold !== '' && maxThreshold !== '' && Number(minThreshold) >= Number(maxThreshold)) {
            setError(t('sensorThresholdModal.invalidRange'));
            return;
        }

        try {
            setLoading(true);
            await api.put(`/sensors/${sensor._id}/threshold`, {
                minThreshold: minThreshold === '' ? null : Number(minThreshold),
                maxThreshold: maxThreshold === '' ? null : Number(maxThreshold),
                thresholdEditableByCompany,
                dataGapAlertEnabled
            });
            toast.success(t('sensorThresholdModal.updated'));
            onSuccess();
        } catch (err) {
            toast.error(err.response?.data?.message || t('sensorThresholdModal.updateFailed'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <AnimatePresence>
            <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onClick={(e) => e.target === e.currentTarget && onClose()}>
                <motion.div className="modal" style={{ maxWidth: 460 }} initial={{ opacity: 0, scale: 0.9, y: 30 }} animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9 }} transition={{ duration: 0.25 }}>

                    <div className="modal-header">
                        <div className="modal-title">
                            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(245,158,11,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--warning)' }}>
                                <MdTune />
                            </div>
                            {t('sensorThresholdModal.title')}
                        </div>
                        <button className="modal-close" onClick={onClose}><MdClose /></button>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                                {t('sensorThresholdModal.desc')}
                            </p>

                            <div className="form-group">
                                <label className="form-label">{t('sensorThresholdModal.minThreshold')}</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    placeholder={t('sensorThresholdModal.minThresholdPlaceholder')}
                                    value={minThreshold}
                                    onChange={(e) => { setMinThreshold(e.target.value); setError(''); }}
                                    disabled={loading}
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">{t('sensorThresholdModal.maxThreshold')}</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    placeholder={t('sensorThresholdModal.maxThresholdPlaceholder')}
                                    value={maxThreshold}
                                    onChange={(e) => { setMaxThreshold(e.target.value); setError(''); }}
                                    disabled={loading}
                                />
                                {error && <div className="form-error">{error}</div>}
                            </div>

                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={thresholdEditableByCompany}
                                    onChange={(e) => setThresholdEditableByCompany(e.target.checked)}
                                    disabled={loading}
                                />
                                {t('sensorThresholdModal.allowCompanyEdit')}
                            </label>

                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer', marginTop: 10 }}>
                                <input
                                    type="checkbox"
                                    checked={dataGapAlertEnabled}
                                    onChange={(e) => setDataGapAlertEnabled(e.target.checked)}
                                    disabled={loading}
                                />
                                {t('sensorThresholdModal.dataGapAlert')}
                            </label>
                            <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '4px 0 0 24px' }}>
                                {t('sensorThresholdModal.dataGapAlertHint')}
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
                                {t('common.cancel')}
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={loading}>
                                {loading ? <><span className="spinner spinner-sm" /> {t('common.saving')}</> : t('sensorThresholdModal.save')}
                            </button>
                        </div>
                    </form>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
